import { Injectable } from "@angular/core";
import { StationData } from "../../../station-track-visualizer/models/model";
import { StationSurveyData } from "../models/model";
import { HTTPAPIConfig } from "../../../../../../usermgmt/src/lib/shared/vl-http/http-api.config";
import { APIConfigMapping } from "../../../../../../usermgmt/src/lib/shared/vl-http/api-mapping-config.cnst";
import { VlHttpService } from "../../../../../../usermgmt/src/lib/shared/vl-http/vl-http.service";
import { VlDataService } from "../../../../../../usermgmt/src/lib/shared/vl-data/vl-data.service";
import { SurveyData } from "../interfaces/survey-data-interface";
import { BehaviorSubject, forkJoin, Subject, Subscription, Observable } from "rxjs";
import { AbstractControl, FormArray, FormGroup } from "@angular/forms";
import { environment } from "../../../../../../usermgmt/src/lib/shared/environments/environment.dev";
import { MessageService } from 'primeng/api';
import { SseService } from '../../../services/sse.service';
import { START_STREAM_ID, SSE_STATUS } from '../../../constants/proj.cnst';
import { AppStateService, isNullOrUndefined } from "usermgmt";
import { ACTION_KEYS, COMPONENT_KEYS, TRACK_PARITY_RULES, TrackParityRule, TRACK_PARITY_ELIGIBLE_ZONES } from "../constants/survey-data.cnst";
import { UndoRedoService } from "../../../services/layout/undo-redo/undo-redo.service";
import { SurveyDataProcessorService } from "./survey-data-processor.service";
import { ActivatedRoute, Router } from "@angular/router";
import { WORKFLOW_STORAGE_KEYS, WorkflowIndexedDbService, type WorkflowStorageKey } from "../../../../../../usermgmt/src/lib/services/workflow-indexed-db.service";
import type { TinsApproachChange } from "../../../station-track-visualizer/models/model";

export interface ChangeDetectionResult {
  regenerateNeeded: boolean;
  updateNeeded: boolean;
  anyChangesDetected: boolean;
}
export interface ComponentActionState {
  update: boolean;
  regenerate: boolean;
}
export interface FieldConfig {
  updateFields: string[];      // Fields that trigger UPDATE
  regenerateFields: string[];  // Fields that trigger REGENERATE
}

/** Locks whose unlock a page in this tab fired but could not confirm before it went away. */
const PENDING_UNLOCKS_KEY = 'pendingLayoutUnlocks';

@Injectable({
  providedIn: 'root'
})
export class StationSurveyStepperService {
  // This service can be used to manage the state of the survey stepper
  private stationDataSubject = new BehaviorSubject<StationData | any>(null);
  stationData$ = this.stationDataSubject.asObservable();

  get stationDataDetails(): StationData | any {
    return this.stationDataSubject.value;
  }

  stationDataValues(data: StationData | any) {
    this.stationDataSubject.next(data);
  }

  // Initialize to empty object instead of null to avoid null checks in components
  stationData: StationSurveyData | any = {};
  merged_tag_tin_data: StationSurveyData | any = {};
  trackParityRule: TrackParityRule = TRACK_PARITY_RULES.NEW;
  // Signalling TOC points when the TOC step first opened in this edit session; Update is needed while they differ.
  signallingTocBaselineJson: string | null = null;

  stampTrackParityRule(target: any, rule: TrackParityRule = this.trackParityRule): any {
    if (!target) return target;
    if (!target.feature_flags) target.feature_flags = {};
    target.feature_flags.track_parity_rule = rule;
    return target;
  }

  getSavedTrackParityRule(explicitStationData?: any): TrackParityRule | null {
    const candidates = explicitStationData
      ? [explicitStationData]
      : [this.stationData, this.surveyData?.stationData];
    for (const obj of candidates) {
      const rule = obj?.feature_flags?.track_parity_rule;
      if (rule === TRACK_PARITY_RULES.NEW || rule === TRACK_PARITY_RULES.OLD) return rule;
    }
    return null;
  }

  initTrackParityRule(explicitStationData?: any): TrackParityRule {
    this.trackParityRule = this.getSavedTrackParityRule(explicitStationData)
      || TRACK_PARITY_RULES.NEW;
    return this.trackParityRule;
  }

  isTrackParityOptionApplicable(explicitStationData?: any, isDraft?: boolean): boolean {
    if (isDraft) return false;
    const rawZone = explicitStationData
      ? (explicitStationData?.zone || explicitStationData?.Zone || explicitStationData?.zone_name || explicitStationData?.zoneName)
      : (this.surveyData?.stationData?.zone || (this.surveyData?.stationData as any)?.Zone || this.stationData?.zone || this.stationData?.Zone || this.stationData?.zone_name || this.stationData?.zoneName);
    const zone = String(rawZone || '').trim().toLowerCase();
    return TRACK_PARITY_ELIGIBLE_ZONES.includes(zone as any);
  }

  applyTrackParityRule(rule: TrackParityRule, explicitStationData?: any): void {
    this.trackParityRule = rule;
    const targets = explicitStationData
      ? [explicitStationData]
      : [this.stationData, this.surveyData?.stationData];
    targets.forEach(target => {
      if (target && typeof target === 'object' && Object.keys(target).length > 0) {
        this.stampTrackParityRule(target, rule);
      }
    });
  }

  private activeIndex: number = 0;
  private maxStepIndex: number = 0;
  availableTracks = [];
  surveyData: SurveyData = {
    stationData: null,
    adjacentStations: [],
    selectedTracks: [],
  };
  sip_image_binary_data: any;
  previous_sip_image_binary_data: any;
  gradientFile: any;
  psrFile: any;
  signallingTOC: any;
  signallingTocFileName: any;
  isPreviewCancelled = false;
  trackRegenerationReqired: boolean = false;
  private previewLayoutDirty = false;
  stationMetaInfo: any = null;
  trackNameOptions = [
    { label: 'Select track', value: '' }
  ];
  selectedSubTrackList: any[] = [];
  selectedVersion: any;

  private updateNeededSubject = new BehaviorSubject<boolean>(false);
  updateNeeded$ = this.updateNeededSubject.asObservable();

  private processingCompletedSubject = new Subject<{ status: boolean, isNavigate?: boolean, station_name: string, station_uuid?: string, station_id?: string, error?: string,result?:any }>();
  processingCompleted$ = this.processingCompletedSubject.asObservable();

  private regenerateNeededSubject = new BehaviorSubject<boolean>(false);
  regenerateNeeded$ = this.regenerateNeededSubject.asObservable();
  public regenerateNeeded: boolean = false;
  private showSipLayoutSubject = new BehaviorSubject<boolean>(false);
  showSipLayout$ = this.showSipLayoutSubject.asObservable();

  private createInitialState(): Record<string, ComponentActionState> {
    return {
      StationDataComponent: { update: false, regenerate: false },
      AdjacentStationsComponent: { update: false, regenerate: false },
      UtilitiesComponent: { update: false, regenerate: false },
      TurnoutsComponent: { update: false, regenerate: false },
      TrapPointsComponent: { update: false, regenerate: false },
      SignalsV2Component: { update: false, regenerate: false },
      DeadEndsComponent: { update: false, regenerate: false },
      KavachTerritoryEndingPointComponent: { update: false, regenerate: false },
      AdjustmentPointsComponent: { update: false, regenerate: false },
      AspectControlChartComponent: { update: false, regenerate: false },
      SignallingTocComponent: { update: false, regenerate: false },
    };
  }
  componentState = new BehaviorSubject<Record<string, ComponentActionState>>(
    this.createInitialState()
  );
  componentState$ = this.componentState.asObservable();


  constructor(
    private readonly httpSrv: VlHttpService,
    private readonly dataSrv: VlDataService,
    private sseService: SseService,
    private messageService: MessageService,
    private appStateService: AppStateService,
    private undoRedoService: UndoRedoService,
    private surveyDataProcessorService: SurveyDataProcessorService,
    private router: Router,
    private route: ActivatedRoute,
    private workflowStorage: WorkflowIndexedDbService
  ) {
    this.surveyData = this.getWorkflowData<SurveyData>(WORKFLOW_STORAGE_KEYS.surveyData, this.surveyData);
    // Also restore stationData to preserve complex arrays (Signal, CROSSING, etc.).
    this.stationData = this.getWorkflowData<StationSurveyData>(WORKFLOW_STORAGE_KEYS.stationData, this.stationData);
    if (this.surveyData.selectedTracks) {
      this.prepareTrackNameOptions()
    }
    this.merged_tag_tin_data = this.getWorkflowData<StationSurveyData>(
      WORKFLOW_STORAGE_KEYS.mergedTagTinData,
      this.merged_tag_tin_data
    );
  }


  getActiveIndex(): number {
    const step = this.getActiveStepFromLocalStorage();
    if (step !== null) {
      this.activeIndex = step;
    }
    return this.activeIndex;
  }

  setActiveIndex(index: number): void {
    this.setActiveStepInLocalStorage(index);
    this.activeIndex = index;
  }

  resetActiveIndex(): void {
    this.activeIndex = 0;
    this.setActiveStepInLocalStorage(this.activeIndex);
  }

  getMaxStepIndex(): number {
    const step = this.getMaxStepFromLocalStorage();
    if (step !== null) {
      this.maxStepIndex = step;
    }
    return this.maxStepIndex;
  }

  setMaxStepIndexIndex(index: number): void {
    this.setMaxStepInLocalStorage(index);
    this.maxStepIndex = index;
  }

  resetMaxStepIndexIndex(): void {
    this.maxStepIndex = 0;
    this.setMaxStepInLocalStorage(this.maxStepIndex);
  }

  nextStep(): void {
    // if(this.activeIndex === 3){ //Just for Local Testing
    //     this.stationData = stationSurveyData;
    //      this.sip_image_binary_data = stationSurveyData.sip_image_base64;
    // }
    this.activeIndex++;
  }

  prepareTrackNameOptions(): { label: string, value: string }[] {
    this.trackNameOptions = [{ label: 'Select track', value: '' }];

    // Direct entry paths can skip the Station Data step, so recover tracks from
    // stationData when selectedTracks has not been populated yet.
    if (!this.surveyData.selectedTracks?.length) {
      const derivedTracks: string[] = this.stationData?.Signal
        ?.map((signal: any) => signal?.track_name)
        ?.filter((track: string | null | undefined): track is string => !!track?.trim()) || [];

      this.surveyData.selectedTracks = [...new Set(derivedTracks)];
    }

    this.surveyData.selectedTracks.forEach(track => {
      let option = { label: track, value: track }
      this.trackNameOptions.push(option);
    });
    return this.trackNameOptions;
  }

  previousStep(): void {
    if (this.activeIndex > 0) {
      this.activeIndex--;
    }
  }

  setActiveStepInLocalStorage(step: number): void {
    localStorage.setItem('activeStep', step.toString());
  }

  getActiveStepFromLocalStorage(): number {
    const step = localStorage.getItem('activeStep');
    return step ? parseInt(step, 10) : 0;
  }

  setMaxStepInLocalStorage(step: number): void {
    localStorage.setItem('maxStep', step.toString());
  }

  getMaxStepFromLocalStorage(): number {
    const step = localStorage.getItem('maxStep');
    return step ? parseInt(step, 10) : 0;
  }


  setWorkflowData<T>(key: WorkflowStorageKey, data: T): void {
    this.syncWorkflowDataInMemory(key, data);
    this.workflowStorage.set(key, data);
  }

  setSurveyDataInLocalStorage(data: SurveyData | any): void {
    this.surveyData = data;
    this.setWorkflowData(WORKFLOW_STORAGE_KEYS.surveyData, data);
  }

  setStationData(stationData: StationSurveyData | any): void {
    this.stationData = stationData;
    this.setWorkflowData(WORKFLOW_STORAGE_KEYS.stationData, stationData);
  }

  getWorkflowData<T>(key: WorkflowStorageKey, fallback: T): T;
  getWorkflowData<T>(key: WorkflowStorageKey): T | null;
  getWorkflowData<T>(key: WorkflowStorageKey, fallback?: T): T | null {
    const storedData = this.workflowStorage.get<T>(key);
    if (!isNullOrUndefined(storedData)) {
      return storedData;
    }
    const memoryData = this.getWorkflowDataFromMemory<T>(key);
    if (!isNullOrUndefined(memoryData)) {
      return memoryData;
    }
    return fallback ?? null;
  }

  private syncWorkflowDataInMemory<T>(key: WorkflowStorageKey, data: T): void {
    switch (key) {
      case WORKFLOW_STORAGE_KEYS.stationData:
        this.stationData = data as StationSurveyData;
        break;
      case WORKFLOW_STORAGE_KEYS.mergedTagTinData:
        this.merged_tag_tin_data = data as StationSurveyData;
        break;
    }
  }

  private getWorkflowDataFromMemory<T>(key: WorkflowStorageKey): T | null {
    switch (key) {
      case WORKFLOW_STORAGE_KEYS.surveyData:
        return this.surveyData as T;
      case WORKFLOW_STORAGE_KEYS.stationData:
        return this.stationData as T;
      case WORKFLOW_STORAGE_KEYS.mergedTagTinData:
        return this.merged_tag_tin_data as T;
      default:
        return null;
    }
  }

  sanitizeMaxCmDistancesMap(entries: any, adjacentStations: any[] = []): any[] {
    if (!Array.isArray(entries)) return [];

    return entries.reduce((result: any[], entry: any, index: number) => {
      if (adjacentStations[index]?.is_block_section_station) {
        return result;
      }

      result.push({
        ...entry,
        station_name:
          typeof entry?.station_name === 'string'
            ? entry.station_name
            : entry?.station_name?.station_name || entry?.station_name?.value?.station_name || ''
      });

      return result;
    }, []);
  }

  removeSurveyDataFromLocalStorage(): void {
    this.previewLayoutDirty = false;
    this.trackParityRule = TRACK_PARITY_RULES.NEW;
    this.surveyData = {
      stationData: null,
      adjacentStations: [],
      selectedTracks: []
    };

    // Clear in-memory file data
    this.sip_image_binary_data = null;
    this.previous_sip_image_binary_data = null;
    this.gradientFile = null;
    this.psrFile = null;
    this.signallingTOC = null;
    this.stationMetaInfo = null;

    localStorage.removeItem('activeStep');
    localStorage.removeItem('maxStep');
    this.workflowStorage.remove(WORKFLOW_STORAGE_KEYS.surveyData);
    localStorage.removeItem('processing_active');
    localStorage.removeItem('processing_station_id');
    localStorage.removeItem('processing_station_name');
    localStorage.removeItem('draftId');
    localStorage.removeItem('isDraftCloned');
  }

  setSIPImage(image: any) {
    this.sip_image_binary_data = image;
  }

  setPreviousSIPImage(image: any): void {
    this.previous_sip_image_binary_data = image;
  }

  setSignallingTOC(file: any) {
    this.signallingTOC = file;
  }

  setSignallingTocFileName(fileName: any): void {
    this.signallingTocFileName = fileName;
  }

  removeStationDataFromLocalStorage(): void {
    this.stationData = {};
    this.trackParityRule = TRACK_PARITY_RULES.NEW;
    this.workflowStorage.remove(WORKFLOW_STORAGE_KEYS.stationData);
    this.tinsApproachSelection = null;
    localStorage.removeItem('stationData');
  }

  /**
   * TIN approach picked in the Track Section toolbar. Kept apart from `stationData` because many
   * steps replace `stationData` with backend responses that may not carry these keys.
   */
  private tinsApproachSelection: { stationUuid: string; values: TinsApproachChange } | null = null;

  setTinsApproachSelection(values: TinsApproachChange): void {
    const stationUuid = this.surveyData?.stationData?.station_uuid;
    this.tinsApproachSelection = stationUuid ? { stationUuid, values: structuredClone(values) } : null;
  }

  /** Writes the picked TIN approach onto `target` (a payload or station data object) and returns it. */
  applyTinsApproachSelection<T>(target: T): T {
    const selection = this.tinsApproachSelection;
    const data = target as any;
    if (!data || !selection || selection.stationUuid !== this.surveyData?.stationData?.station_uuid) return target;
    const { overlap_count, ...rest } = structuredClone(selection.values);
    Object.assign(data, rest);
    if (overlap_count === null) {
      delete data.overlap_count;
    } else {
      data.overlap_count = overlap_count;
    }
    return target;
  }

  // uploadSipAndGradient(postdata: any) {
  //   console.log('VAXXUploading SIP and Gradient with postdata:', postdata);
  //   const apiConfig: HTTPAPIConfig = this.dataSrv.deepClone(APIConfigMapping['UPLOAD_SIP_AND_GRADIENT']);
  //   apiConfig.postData = postdata;
  //   return this.httpSrv.placeAPICall(apiConfig);
  // }
  uploadSipAndGradient(postdata: any) {
    const apiConfig: HTTPAPIConfig =
      this.dataSrv.deepClone(APIConfigMapping['UPLOAD_SIP_AND_GRADIENT']);

    apiConfig.postData = {
      ...postdata,
      callback_url: environment.YOLO_CALLBACK_URL
    };

    return this.httpSrv.placeAPICall(apiConfig);
  }

  fetchPIMData(postdata: any) {
    const gradientApiConfig: HTTPAPIConfig = this.dataSrv.deepClone(APIConfigMapping['GET_PIM_GRADIENT_FILE']);
    gradientApiConfig.postData = postdata;

    const sipApiConfig: HTTPAPIConfig = this.dataSrv.deepClone(APIConfigMapping['GET_PIM_SIP_FILE']);
    sipApiConfig.postData = postdata;

    const signallingTocApiConfig: HTTPAPIConfig = this.dataSrv.deepClone(APIConfigMapping['GET_PIM_SIGNALLING_FILE']);
    signallingTocApiConfig.postData = postdata;

    return forkJoin({
      gradientPdf: this.httpSrv.placeAPICall(gradientApiConfig),
      sipImage: this.httpSrv.placeAPICall(sipApiConfig),
      signallingTOC: this.httpSrv.placeAPICall(signallingTocApiConfig)
    });
  }

  // Set update needed globally, but only if no regenerate is needed anywhere
 setUpdateNeeded(value: boolean) {
  // Update allowed only if no regenerate outside the components that offer both
  this.updateNeededSubject.next(!this.hasUpdateBlockingRegenerate() && value);
}

  /* Signals and utilities raise both actions for some edits so the user can choose,
     so their regenerate does not suppress update the way the other steps do */
  private readonly BOTH_ACTIONS_COMPONENTS: string[] = [COMPONENT_KEYS.SIGNALS, COMPONENT_KEYS.UTILITIES,  COMPONENT_KEYS.STATION_DATA];

  private hasUpdateBlockingRegenerate(): boolean {
    return Object.entries(this.componentState.value)
      .some(([componentName, state]) => state.regenerate && !this.BOTH_ACTIONS_COMPONENTS.includes(componentName));
  }

  // // Set regenerate needed globally, takes priority
   setRegenerateNeeded(value: boolean) {
    this.regenerateNeeded = value;
    this.regenerateNeededSubject.next(value);
   }

  
  getRegenerateNeeded(): boolean {
    return Object.values(this.componentState.value)
      .some(state => state.regenerate);
  }

  getUpdateNeeded(): boolean {
    if (this.hasUpdateBlockingRegenerate()) {
      return false;
    }
    return Object.values(this.componentState.value).some(state => state.update);
  }

  resetUpdateAndRegenerate() {
    this.updateNeededSubject.next(false);
    this.regenerateNeededSubject.next(false);
    this.componentState.next(this.createInitialState());
    this.previewLayoutDirty = false;
    this.signallingTocBaselineJson = null;
  }

 setPreviewLayoutDirty(value: boolean): void {
    this.previewLayoutDirty = value;
  }

 hasUnsavedChanges(): boolean {
    return this.previewLayoutDirty || this.trackRegenerationReqired || this.getRegenerateNeeded() || this.getUpdateNeeded();
  }

  /**
   * Normalize values for consistent deep comparison.
   */
  private normalizeValue(value: any): any {
    if (value === null || value === undefined || value === '') return null;

    const numericValue = Number(value);
    if (!isNaN(numericValue)) return +numericValue.toFixed(9);

    if (Array.isArray(value))
      return value.map(v => this.normalizeValue(v));

    if (typeof value === 'object')
      return Object.fromEntries(
        Object.entries(value).map(([key, val]) => [key, this.normalizeValue(val)])
      );

    return value;
  }


  /**
   * Deep equality check for normalized values.
   */
  private deepEqual(valA: any, valB: any): boolean {
    valA = this.normalizeValue(valA);
    valB = this.normalizeValue(valB);

    if (valA === valB) return true;
    if (typeof valA !== typeof valB) return false;

    if (Array.isArray(valA)) {
      if (valA.length !== valB.length) return false;
      return valA.every((item, i) => this.deepEqual(item, valB[i]));
    }

    if (valA && typeof valA === 'object') {
      const keysA = Object.keys(valA);
      const keysB = Object.keys(valB);
      if (keysA.length !== keysB.length) return false;
      return keysA.every(key => this.deepEqual(valA[key], valB[key]));
    }

    return false;
  }


  /**
   * Detect changes for a single row form.
   */
  detectSingleForm(formGroup: FormGroup, config: FieldConfig): ChangeDetectionResult {
    const originalJson = formGroup.get('__originalJson')?.value || '{}';
    const original = this.normalizeValue(JSON.parse(originalJson));

    const { __originalJson, ...currentRaw } = formGroup.value;
    const current = this.normalizeValue(currentRaw);

    const hasFieldChanged = (fields: string[]) =>
      fields.some(field => !this.deepEqual(original[field], current[field]));

    if (hasFieldChanged(config.regenerateFields)) {
      return { regenerateNeeded: true, updateNeeded: false, anyChangesDetected: true };
    }

    if (hasFieldChanged(config.updateFields)) {
      return { regenerateNeeded: false, updateNeeded: true, anyChangesDetected: true };
    }

    return { regenerateNeeded: false, updateNeeded: false, anyChangesDetected: false };
  }


  /**
   * Detect & apply regenerate/update flags across formArray.
   */
  detectAndApplyChanges(
    componentName: string,
    formArray: FormArray,
    config: FieldConfig,
    initialFlags: { updateNeeded: boolean; regenerateNeeded: boolean }
  ): void {

    let regenerateRequired = false;
    let updateRequired = false;
    let anyChangeDetected = false;

    for (const control of formArray.controls) {
      const result = this.detectSingleForm(control as FormGroup, config);

      anyChangeDetected ||= result.anyChangesDetected;

      if (result.regenerateNeeded) {
        regenerateRequired = true;
        this.updateComponentState(componentName, ACTION_KEYS.REGENERATE)
        break;
      }

      if (result.updateNeeded) {
        updateRequired = true;
        this.updateComponentState(componentName, ACTION_KEYS.UPDATE)
      }
    }

    if (!anyChangeDetected) {
      this.setRegenerateNeeded(initialFlags.regenerateNeeded);
      this.setUpdateNeeded(initialFlags.updateNeeded);
      this.updateState(componentName, { update: false, regenerate: false })
      return;
    }

    // Keep regenerate ON if already enabled (priority over update)
    this.setRegenerateNeeded(regenerateRequired || this.getRegenerateNeeded());
    this.setUpdateNeeded(updateRequired);
    //this.setUpdateNeeded(!regenerateRequired && updateRequired);
    if (regenerateRequired || updateRequired) {
      this.trackRegenerationReqired = true;
    };
  }

  getSelectedSubTrackList(): any {
    return this.selectedSubTrackList;
  }

  stationDataHolder: any;
  presets(stationData: any,isDarft:boolean = false) {
    this.setWorkflowData(WORKFLOW_STORAGE_KEYS.mergedTagTinData, structuredClone(stationData));
    this.stationDataHolder = structuredClone(stationData);
    this.resetActiveIndex();
    this.removeStationDataFromLocalStorage();
    if(!isDarft){
      this.removeSurveyDataFromLocalStorage();
    }
    stationData = this.surveyDataProcessorService.swapTrapPointsDisplayTOAbs(stationData);
    stationData = this.surveyDataProcessorService.swapDeadEndPointsDisplayTOAbs(stationData);
    this.stationData = stationData;
    this.initTrackParityRule(stationData);
    const stationDetails: any = structuredClone(stationData);
    this.setWorkflowData(WORKFLOW_STORAGE_KEYS.stationData, stationDetails);
  }
  
  setPreviewCancelled(value: boolean) {
    this.isPreviewCancelled = value;
  }

  private processingSubscription: Subscription | null = null;
  private processingConnectionId: string | null = null;
  private listeningStartTime: number = 0;

  stopListeningForStationProcessing(): void {
    this.processingSubscription?.unsubscribe();
    this.processingSubscription = null;
    this.processingConnectionId = null;
  }

  listenForStationProcessing(connectionId: string, notNavigate = true, startStreamId = START_STREAM_ID, forceReconnect = false): void {
    const explicitStartTime = Number.parseInt(startStreamId.split('-')[0], 10);
    this.listeningStartTime = startStreamId !== START_STREAM_ID && Number.isFinite(explicitStartTime)
      ? explicitStartTime : Date.now();

    if (this.processingSubscription && !this.processingSubscription.closed) {
      if (this.processingConnectionId === connectionId && !forceReconnect) {
        return;
      }
      this.processingSubscription.unsubscribe();
      this.processingSubscription = null;
      this.processingConnectionId = null;
    }

    const sseEndpoint = `${connectionId}/stream`;
    this.processingConnectionId = connectionId;
    this.processingSubscription = this.sseService.connect(sseEndpoint, { start_stream_id: startStreamId, presence_conn_id: connectionId }).subscribe({
      next: (message) => {
        let data = message.data;
        const normalize = (value: any) => String(value ?? '').trim().toLowerCase();

        if (typeof data === 'string') {
          if (data.trim().startsWith('data:')) {
            data = data.replace(/^data:\s*/, '');
          }

          try {
            data = JSON.parse(data);
          } catch (e) {
             const statusMatch = data.match(/\\?"status\\?":\s*\\?"([^\\"]+)\\?"/);
             const uuidMatch = data.match(/\\?"station_uuid\\?":\s*\\?"([^\\"]+)\\?"/);
             const nameMatch = data.match(/\\?"station_name\\?":\s*\\?"([^\\"]+)\\?"/);
             const stationResult = this.getStationResult(message.data);
             const idMatch = data.match(/\\?"station_id\\?":\s*\\?"([^\\"]+)\\?"/);
             const errorMatch = data.match(/\\?"error\\?":\s*\\?"([^\\"]+)\\?"/);
             const timestampMatch = data.match(/\\?"station_processed_timestamp_ms\\?":\s*(\d+)/);

             if (statusMatch && statusMatch[1]) {
                data = {
                  status: statusMatch[1],
                  station_uuid: uuidMatch ? uuidMatch[1] : null,
                  station_id: idMatch ? idMatch[1] : null,
                  station_name: nameMatch ? nameMatch[1] : null,
                  station_result: stationResult ,
                  error: errorMatch ? errorMatch[1] : null,
                  station_processed_timestamp_ms: timestampMatch ? parseInt(timestampMatch[1], 10) : 0
                };
             }
          }
        }

        // Extract timestamp from SSE event ID (Redis stream ID format: "milliseconds-sequence")
        const eventIdTimestamp = message.id ? parseInt(message.id.split('-')[0], 10) : 0;
        const isStaleEvent = eventIdTimestamp > 0 && eventIdTimestamp < this.listeningStartTime;
        const eventType = normalize(data?.type);
        const status = normalize(data?.status);
        const isProcessingCompleted =
          eventType === 'final_extracted' ||
          status === 'completed' ||
          status === normalize(SSE_STATUS.PROCESSING_COMPLETED);
        const stationUuid = data?.station_uuid ?? data?.station_result?.station_uuid;
        const stationId = data?.station_id ?? data?.station_result?.station_id;
        const stationName = data?.station_name ?? data?.station_result?.station_name;
        const stationResult = data?.station_result ?? data?.result;

        if (data && isProcessingCompleted) {
          const eventTimestamp = data.station_processed_timestamp_ms;
          if (eventTimestamp && eventTimestamp <= this.listeningStartTime) {
            console.warn(`Ignoring stale SSE event. Event time: ${eventTimestamp}, Listening start: ${this.listeningStartTime}`);
            return;
          }

          if (!this.isPreviewCancelled) { this.messageService.add({severity: 'success', summary: 'Success', detail: `Processing completed successfully for ${stationName}.`});}
          this.processingCompletedSubject.next({
            status: true,
            isNavigate: notNavigate,
            station_name: stationName,
            station_uuid: stationUuid,
            station_id: stationId,
            result: stationResult
          });
          if (this.processingSubscription) {
            this.processingSubscription.unsubscribe();
            this.processingSubscription = null;
            this.processingConnectionId = null;
          }
        } else if (data && data.error && !isProcessingCompleted) {
          if (isStaleEvent) {
            console.warn(`Ignoring stale SSE error event. Event ID: ${message.id}, Listening start: ${this.listeningStartTime}`);
            return;
          }

          this.messageService.add({ severity: 'error', summary: 'Error', detail: `Processing failed for ${stationName}. Error: ${data.error}` });
          this.processingCompletedSubject.next({
            status: false,
            isNavigate: notNavigate,
            station_name: stationName,
            station_uuid: stationUuid,
            station_id: stationId,
            error: data.error,
            result: stationResult
          });
          if (this.processingSubscription) {
            this.processingSubscription.unsubscribe();
            this.processingSubscription = null;
            this.processingConnectionId = null;
          }
           
        }
      },
      error: (err) => {
        console.error("SSE Error in Service", err);
        this.processingSubscription = null;
        this.processingConnectionId = null;
      },
      complete: () => {
        this.processingSubscription = null;
        this.processingConnectionId = null;
      },
    });
  }

  //  - lockOpQueue makes lock/unlock operations happen one after another for one station, so the
  //    next request is only sent once the previous one has responded. Server-side
  //    ordering then always matches the order the UI asked for.
  //  - lockIntent prevents unnecessary duplicate lock/unlock requests
  //    It tells - What is the latest state the UI has requested?
  private lockOpQueue = new Map<string, Promise<void>>();
  private lockIntent = new Map<string, 'locked' | 'unlocked'>();

  private getLockKey(stationUuid: string, draftUuid: string): string {
    return draftUuid ? `${stationUuid}::${draftUuid}` : stationUuid;
  }

  /**
   * Runs `op` after every lock operation already queued for this station has
   * settled. Failures are swallowed here (each op reports its own error) so one
   * failed call never stalls the queue.
   */
  private enqueueLockOp(key: string, op: () => Observable<any>): Promise<void> {
    const previous = this.lockOpQueue.get(key) ?? Promise.resolve();
    const current = previous.then(
      () => new Promise<void>((resolve) => {
        op().subscribe({
          next: () => resolve(),
          error: () => resolve(),
          complete: () => resolve()
        });
      })
    );
    this.lockOpQueue.set(key, current);
    current.then(() => {
      // Drop the entry only if nothing else queued behind it in the meantime.
      if (this.lockOpQueue.get(key) === current) this.lockOpQueue.delete(key);
    });
    return current;
  }

  lockStationLayout(stationUuid: string, draftUuid: string = ''): void {
    if (!stationUuid) return;
    const key = this.getLockKey(stationUuid, draftUuid);
    // Already locked by this session — a second lock adds nothing and only
    // creates a request that can overtake a later unlock.
    if (this.lockIntent.get(key) === 'locked') return;
    this.lockIntent.set(key, 'locked');

    const apiConfig: HTTPAPIConfig = this.dataSrv.deepClone(APIConfigMapping['LOCK_STATION_LAYOUT']);
    let userInfo = this.appStateService.getUserInfoFromLocalStorage();
    apiConfig.postData = {
      station_uuid: stationUuid,
      editor_name: userInfo?.username || "Unknown",
      ...(draftUuid ? { draft_uuid: draftUuid } : {})
    }
    this.enqueueLockOp(key, () => new Observable((observer) => {
      const sub = this.httpSrv.placeAPICall(apiConfig).subscribe({
        next: () => { observer.next(null); observer.complete(); },
        error: (error: any) => {
          // The lock did not take effect, so do not suppress a later retry.
          if (this.lockIntent.get(key) === 'locked') this.lockIntent.delete(key);
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error in locking the station layout' });
          observer.complete();
        }
      });
      return () => sub.unsubscribe();
    }));
  }

  unlockStationLayout(stationUuid: string, draftUuid: string = ''): Promise<void> {
    this.undoRedoService.pendingUndoState = null; // Clear pending undo state when unlocking
    if (!stationUuid) return Promise.resolve();
    const key = this.getLockKey(stationUuid, draftUuid);
    if (this.lockIntent.get(key) === 'unlocked') return Promise.resolve();
    this.lockIntent.set(key, 'unlocked');

    const apiConfig: HTTPAPIConfig = this.dataSrv.deepClone(APIConfigMapping['UNLOCK_STATION_LAYOUT']);
    let userInfo = this.appStateService.getUserInfoFromLocalStorage();
    apiConfig.postData = {
      station_uuid: stationUuid,
      editor_name: userInfo?.username || 'Unknown',
      ...(draftUuid ? { draft_uuid: draftUuid } : {})
    }
    return this.enqueueLockOp(key, () => new Observable((observer) => {
      const sub = this.httpSrv.placeAPICall(apiConfig).subscribe({
        next: () => { observer.next(null); observer.complete(); },
        error: () => {
          if (this.lockIntent.get(key) === 'unlocked') this.lockIntent.delete(key);
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error in Updating the status of station' });
          observer.complete();
        }
      });
      return () => sub.unsubscribe();
    }));
  }

  waitForLockOps(stationUuid: string, draftUuid: string = ''): Promise<void> {
    return this.lockOpQueue.get(this.getLockKey(stationUuid, draftUuid)) ?? Promise.resolve();
  }

  getHeldLocks(): Array<[string, string]> {
    return Array.from(this.lockIntent.entries())
      .filter(([, intent]) => intent === 'locked')
      .map(([key]) => {
        const [stationUuid, draftUuid = ''] = key.split('::');
        return [stationUuid, draftUuid] as [string, string];
      });
  }

  /**
   * Find every layout lock this browser session currently owns, send unlock requests for them, and wait until either all unlocks finish or 4 seconds pass.
   *
   * Logout wipes localStorage and hard-reloads the app, so the component
   * `ngOnDestroy`/`pagehide` unlocks either never run or run without a token and
   * are rejected. The station then stays locked until the lock expires, and the
   * same user coming back is told "Station is being edited by <themselves>".
   *
   * Awaiting is required: Normally firing the requests before logout is not enough.
   * `window.location.replace` aborts them in flight (the browser reports status
   * "unknown" with no response). The wait is capped to 4s so a
   * slow or dead lock API can never trap the user on the page.
   */
  releaseHeldLocks(timeoutMs: number = 4000): Promise<void> {
    const pending = this.getHeldLocks()
      .map(([stationUuid, draftUuid]) => this.unlockStationLayout(stationUuid, draftUuid));
    if (!pending.length) return Promise.resolve();
    // Wait for whichever happens first: all unlock requests finish OR the timeout expires.
    return Promise.race([
      Promise.all(pending).then(() => undefined),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))
    ]);
  }

  /**
   * Logout clears sessionStorage, so a different user signing in on this tab never
   * inherits someone else's pending unlocks.
   */
  markUnlocksPending(): void {
    const heldLocks = this.getHeldLocks();
    if (heldLocks.length) sessionStorage.setItem(PENDING_UNLOCKS_KEY, JSON.stringify(heldLocks));
  }

  releasePendingUnlocks(): Promise<void> {
    const pending = sessionStorage.getItem(PENDING_UNLOCKS_KEY);
    if (!pending) return Promise.resolve();
    sessionStorage.removeItem(PENDING_UNLOCKS_KEY);
    try {
      for (const [stationUuid, draftUuid] of JSON.parse(pending) as Array<[string, string]>) {
        this.lockIntent.set(this.getLockKey(stationUuid, draftUuid), 'locked');
      }
    } catch {
      return Promise.resolve();
    }
    return this.releaseHeldLocks();
  }

  refreshStationLayoutLockStatus(stationUuid: string, draftUuid: string = ''): any {
    if (!stationUuid) return;
    const key = this.getLockKey(stationUuid, draftUuid);
    // A heartbeat that overtakes an unlock would re-extend a lock nobody holds,
    // so it goes through the same queue.
    if (this.lockIntent.get(key) === 'unlocked') return;

    const apiConfig: HTTPAPIConfig = this.dataSrv.deepClone(APIConfigMapping['REFRESH_STATION_LAYOUT_LOCK']);
    let userInfo = this.appStateService.getUserInfoFromLocalStorage();
    apiConfig.postData = {
      station_uuid: stationUuid,
      editor_name: userInfo?.username || 'Unknown',
      ...(draftUuid ? { draft_uuid: draftUuid } : {})
    }
    this.enqueueLockOp(key, () => new Observable((observer) => {
      const sub = this.httpSrv.placeAPICall(apiConfig).subscribe({
        next: () => { observer.next(null); observer.complete(); },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error in refreshing the lock status of station' });
          observer.complete();
        }
      });
      return () => sub.unsubscribe();
    }));
  }

  updateState(
    componentName: string,
    data?: Partial<ComponentActionState>
  ) {
    const current = this.componentState.value;

    const existingState: ComponentActionState =
      current[componentName] || { update: false, regenerate: false };

    this.componentState.next({
      ...current,
      [componentName]: {
        ...existingState,
        ...data
      }
    });
  }


  updateComponentState(componentName: string, key: string) {
    this.updateState(componentName, {
      [key]: true
    });
  }

  resetComponentState(componentName: string) {
    this.updateState(componentName, {
      update: false,
      regenerate: false
    });
  }

  removeDraftDataFromLocalStorage() {
    localStorage.removeItem('isDraftCloned');
  }

  uploadPsrFile(postdata: any) {
    const apiConfig: HTTPAPIConfig = this.dataSrv.deepClone(APIConfigMapping['EXTRACT_PSR_EXCEL'])
    apiConfig.postData = postdata;
    return this.httpSrv.placeAPICall(apiConfig);
  }

  onUploadGradientFile(postdata: any) {
    const apiConfig: HTTPAPIConfig = this.dataSrv.deepClone(APIConfigMapping['ON_GRADIENT_FILE_UPLOAD']);
    apiConfig.postData = postdata;
    return this.httpSrv.placeAPICall(apiConfig);
  }


  getStationIpAddress(stationData: any) {
    const apiConfig: HTTPAPIConfig = this.dataSrv.deepClone(APIConfigMapping['GET_STATION_IP_ADDRESS'])
    apiConfig.options = {
      params: stationData
    }
    return this.httpSrv.placeAPICall(apiConfig);
  }

  getStationResult(raw: string): any | null {
    if (!raw || typeof raw !== 'string') return null;

    // Handle both raw SSE lines ("data: {...}") and already-extracted JSON strings ("{...}")
    let jsonPart = raw.trim();
    const dataIndex = jsonPart.indexOf('data:');
    if (dataIndex !== -1) {
      jsonPart = jsonPart.slice(dataIndex + 5).trim();
    }

    // 2. The JSON is escaped — unescape it
    jsonPart = jsonPart.replace(/\\"/g, '"');

    // 3. Parse JSON
    let parsed;
    try {
      parsed = JSON.parse(jsonPart);
    } catch (e) {
      // console.error('JSON parse failed', e);
      return null;
    }
      
    // 4. Return what you actually want
    return parsed.station_result ?? null;
  }

  //Draft Generate Store.
  previewStationStore: any = {};

  trackSectionSnapshot: any = null;

    private draftStationDataSubject = new BehaviorSubject<StationData | any>(null);
  draftStationDataObservable$ = this.draftStationDataSubject.asObservable();

  updateDraftStationData(){
    this.draftStationDataSubject.next(null);
  }

  setDraftId(draftId: string) {
    this.router.navigate([], {
      queryParams: { draftId },
      queryParamsHandling: 'merge' // keeps existing params
    });
  }

  getDraftId() {
    return this.route.snapshot.queryParamMap.get('draftId');
  }

  updateAssetAllocationDetails(isDelete: boolean, uuid: string, modeType?: string, type?: string): void {
    if (!uuid || uuid === '') return;
    const stationData = this.getWorkflowData<StationSurveyData>(WORKFLOW_STORAGE_KEYS.stationData);
    if (!stationData) return;

    if (!stationData?.asset_allocation_mapping) {
      stationData.asset_allocation_mapping = {};
    }
    const allocationObj: any = { [uuid]: { action: isDelete ? 'delete' : 'add', mode: modeType, type: type } };
    stationData.asset_allocation_mapping = { ...stationData.asset_allocation_mapping, ...allocationObj };
    this.setWorkflowData(WORKFLOW_STORAGE_KEYS.stationData, stationData);
  }

  setShowSipLayout(value: boolean): void {
    this.showSipLayoutSubject.next(value);
  }

  getShowSipLayout(): boolean {
    return this.showSipLayoutSubject.value;
  }


getMatchedOemFromLocalStorage(stationUuid?: string): string {
  if (!stationUuid) return '';

  const persistedStationData = this.getWorkflowData<any>(WORKFLOW_STORAGE_KEYS.stationData);
  const persistedStationUuid =  persistedStationData?.['Station Uuid'] || persistedStationData?.station_uuid;
  if (persistedStationUuid !== stationUuid) return '';
  return typeof persistedStationData?.oem === 'string' ? persistedStationData.oem : '';
}

 checkifCrossingsIsLadder(cross:any,stationData:any){
  let stationTracks : string[] = [];

  if(stationData.track_start_end_points){
     stationTracks  = stationData.track_start_end_points.map((track:any) => track.track_name );
  }else if(stationData.Signal){
    stationTracks = stationData.Signal?.map((signal:any) => signal.track_name) || [];
  }
  const track1InList = stationTracks.includes(cross['Track 1'] || cross?.track1);
  const track2InList = stationTracks.includes(cross['Track 2'] || cross?.track2);
  const isNormalCrossing = track1InList && track2InList;

  return !isNormalCrossing;
}


convertAllCrossingToProperOrder(crossing:any,stationData: any): any {
    if (!stationData['CROSSING'] || !Array.isArray(stationData['CROSSING'])) return [];
    // let updatedCrossingList: any[] = [];
    // for (const cross of stationData['CROSSING']) {
      if (this.checkifCrossingsIsLadder(crossing, stationData)) { 
        // updatedCrossingList.push(crossing);
        return crossing; }
        let tracks =  [];
        if (stationData.track_start_end_points) {
          tracks = stationData.track_start_end_points.map((track: any) => track.track_name);
        } else if (stationData.Signal) {
          tracks = stationData.Signal?.map((signal: any) => signal.track_name) || [];
        }
      const track1Index: number = tracks.findIndex((item: any) => item === (crossing["Track 1"] || crossing?.track1));
      const track2Index: number = tracks.findIndex((item: any) => item === (crossing["Track 2"] || crossing?.track2));
      const isTrack1Above: boolean = track1Index < track2Index;
      let cross = isTrack1Above ? crossing : this.swapCrossingTrackValues(crossing);
    // }
    return cross;
  }

  swapCrossingTrackValues(data: any): any {
    console.warn(`Swapping track values for crossing ${data.asset_id} to maintain consistent order.`);
    const updated = structuredClone(data);
    [['track1', 'track2'], ['track1Location', 'track2Location'],
      ['track1SipAssetId', 'track2SipAssetId'],
      ['displayTrack1Location', 'displayTrack2Location'], ['displayTrack1AssetId', 'displayTrack2AssetId'],
      ['Track 1 Attached', 'Track 2 Attached'], ['track_1_non_kavach', 'track_2_non_kavach'],
      ['track_1_non_kavach_asset_id', 'track_2_non_kavach_asset_id'], ['track_1_non_kavach_label', 'track_2_non_kavach_label'],
      ['Track_1_offset_Location', 'Track_2_offset_Location'], ['Track_1_coords', 'Track_2_coords'], ['track1Arrow', 'track2Arrow'],
      ['track1Bend', 'track2Bend']
    ].forEach(([key1, key2]) => {
      [updated[key1], updated[key2]] = [updated[key2], updated[key1]];
    });
    if(updated.tin_numbers && Array.isArray(updated.tin_numbers) && updated.tin_numbers.length >= 2){
    const firstArray: any = updated.tin_numbers[1];
    updated.tin_numbers[1] = updated.tin_numbers[0];
    updated.tin_numbers[0] = firstArray;
    }
    return updated;
  }

  private onProceedToFinalLayoutClick = new Subject<any>();
  
  /* Emit data */
  emitProceedToFinalLayoutClickEvent(data: boolean) {
    this.onProceedToFinalLayoutClick.next(data);
  }

  /* Observable for subscribers */
  getProceedToFinalLayoutClick$(): Observable<any> {
    return this.onProceedToFinalLayoutClick.asObservable();
  }

  // Signal to open in the Signals step when jumping from the preview layout
  signalToOpen$ = new BehaviorSubject<{ trackIndex: number; signalIndex: number; signalId?: string } | null>(null);

  replaceValuesInJson(data: any, replacements: Record<string, any>): any {
    // Replace primitive values
    if (typeof data !== 'object' || data === null) {
      return replacements.hasOwnProperty(data) ? replacements[data] : data;
    }
    // Replace arrays
    if (Array.isArray(data)) {
      return data.map(item => this.replaceValuesInJson(item, replacements));
    }
    // Replace objects
    const result: any = {};
    Object.entries(data).forEach(([key, value]) => {
      const newKey = replacements.hasOwnProperty(key) ? replacements[key] : key;
      result[newKey] = this.replaceValuesInJson(value, replacements);
    });

    return result;
  }

  getFilteredIndexes(controls: AbstractControl[], searchTermValue: string, keys: string[]): Set<number> {
    if (!searchTermValue) {
      return new Set(controls.map((_, index) => index));
    }

    return new Set(
      controls.reduce<number[]>((matchedIndexes, control, index) => {
        const isMatch = keys.some(key => this.matchesPath(control.value, key.split('.'), searchTermValue));
        if (isMatch) matchedIndexes.push(index);
        return matchedIndexes;
      }, [])
    );
  }

  /**
   * Walks a dotted path (e.g. 'tracks.loc.locations') through a value.
   * Fans out over arrays at any depth in the path, and matches the
   * search term against whatever leaf value(s) it reaches.
   */
  matchesPath(value: any, pathSegments: string[], searchTermValue: string): boolean {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) {
      return value.some(item => this.matchesPath(item, pathSegments, searchTermValue));
    }
    if (pathSegments.length === 0) {
      return value.toString().toLowerCase().includes(searchTermValue);
    }
    const [currentKey, ...remainingSegments] = pathSegments;
    return this.matchesPath(value?.[currentKey], remainingSegments, searchTermValue);
  }
}
